import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Button, Grid, Stack, Step, StepLabel, Stepper } from '@mui/material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { useAddTournamentMutation, useEditTournamentMutation } from 'src/services/tournaments';
import { Tournament } from 'src/services/tournaments/getTournaments';
import useToast from 'src/hooks/useToast';
import { useSelector } from 'src/store/Store';
import { useTournamentFormValidationSchema } from './tournamentFormValidationSchema';
import BlankCard from 'src/components/shared/BlankCard';
import { Drawer, DrawerHeader } from 'src/components/shared';
import { TournamentFormValues } from '../../types';
import BasicDetails from './BasicDetails';
import AdditionalInfo from './AdditionalInfo';
import { paymentMethodsArr, TournamentsTypes } from 'src/services/tournaments/addTournament';
import toast from 'react-hot-toast';
import useLang from 'src/hooks/useLang';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedTournament?: Tournament;
  loadTournaments: (rowsPerPage: number, page: number) => void;
  page?: number;
}

const steps = (t: any) => [
  t('TournamentsScreen.basicDetails'),
  t('TournamentsScreen.additionalInfo'),
];

const createFormData = (
  values: TournamentFormValues,
  isAnyCatrgoryOfTypeRoundRobinSingleElimination: boolean,
) => {
  const formData = new FormData();
  formData.append('name', values.name);
  formData.append('name_ar', values.name_ar);
  formData.append('venue', values.venue);
  formData.append('stadium_id', values.stadium_id.toString());
  formData.append('tournament_type_id', values.tournament_type_id.toString());
  formData.append('sport_type_id', values.sport_type_id.toString());
  formData.append('start_date', values.start_date);
  formData.append('end_date', values.end_date);
  formData.append('registration_deadline', values.registration_deadline);
  formData.append('price_per_team', values.price_per_team);
  formData.append('is_live', values.is_live.toString());
  formData.append('is_global', values.is_global.toString());
  formData.append('is_private', values.is_private.toString());
  formData.append('gender', values.gender);
  formData.append('prizes', values.prizes);
  formData.append('daily_timings', values.daily_timings);
  formData.append('additional_info', values.additional_info);
  formData.append('rank_type_id', values.rank_type_id?.toString() || '');
  formData.append('rank_id', values.rank_id?.toString() || '');
  formData.append('interface_type', values.interface_type || '');

  if (values.image && typeof values.image !== 'string') {
    formData.append('image', values.image as File);
  }

  values.supported_payment_methods.forEach((method, index) => {
    formData.append(`supported_payment_methods[${index}]`, method.value);
  });

  values.categories.forEach((category, index) => {
    formData.append(`categories[${index}][tournament_category_id]`, category.id.toString());
    formData.append(`categories[${index}][max_number_of_teams]`, category.max_number_of_teams);
    formData.append(`categories[${index}][show_schedule]`, category.show_schedule ? '1' : '0');
    if (category.type !== undefined && category.type !== null) {
      formData.append(`categories[${index}][tournament_type_id]`, category.type.toString());
    }
  });

  if (
    values.tournament_type_id === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION ||
    isAnyCatrgoryOfTypeRoundRobinSingleElimination
  ) {
    formData.append('teams_per_group', values.teams_per_group.toString());
    formData.append('teams_to_qualify_per_group', values.teams_to_qualify_per_group.toString());
  }

  formData.append('is_waiting_list', values.is_waiting_list.toString());
  formData.append('is_golden_point', values.is_golden_point.toString());
  formData.append('is_third_set_tie_breaker', values.is_third_set_tie_breaker.toString());
  formData.append('show_teams', values.show_teams.toString());
  formData.append('schedule_notifications', values.schedule_notifications.toString());

  return formData;
};

const AddEditTournamentForm = ({
  open,
  onClose,
  selectedTournament,
  loadTournaments,
  page,
}: Props) => {
  const [activeStep, setActiveStep] = React.useState(0);

  const [addTournament] = useAddTournamentMutation();
  const [updateTournament] = useEditTournamentMutation();

  const { validationSchema } = useTournamentFormValidationSchema();
  const { toastPromise } = useToast();
  const { getTranslatedTitle } = useLang();

  const scrollRef = useRef<HTMLDivElement>(null);

  const stadium = useSelector((state) => state.configReducer.selectedStadium);
  const options = useSelector((state) => state.tournamentReducer.options);

  const formik = useFormik<TournamentFormValues>({
    initialValues: {
      name: '',
      name_ar: '',
      venue: 'Not specified',
      stadium_id: stadium?.id || 0,
      tournament_type_id: 1,
      sport_type_id: options?.data?.sport_types?.[0]?.id || 0,
      categories: [],
      start_date: '',
      end_date: '',
      registration_deadline: '',
      price_per_team: '',
      teams_per_group: 0,
      teams_to_qualify_per_group: 0,
      is_live: 0,
      is_global: 0,
      is_private: 0,
      gender: '',
      prizes: '',
      daily_timings: '',
      additional_info: '',
      supported_payment_methods: [],
      image: null,
      rank_type_id: selectedTournament?.rank_type?.id || 0,
      rank_id: selectedTournament?.rank?.id || 0,
      interface_type: '1',
      is_waiting_list: 0,
      is_golden_point: 0,
      is_third_set_tie_breaker: 0,
      show_teams: 0,
      schedule_notifications: 0,
    },
    validationSchema: validationSchema(activeStep),
    onSubmit: (values) => {
      if (activeStep === 0) {
        const categoryWithEmptyMaxNumberOfTeams = values?.categories?.find(
          (category) => category.max_number_of_teams === '',
        );
        if (categoryWithEmptyMaxNumberOfTeams) {
          toast.error(
            t('TournamentsScreen.teamLimitForAllCategories', {
              categoryName: getTranslatedTitle(categoryWithEmptyMaxNumberOfTeams, 'name'),
            }),
          );

          return;
        }

        setActiveStep((prev) => prev + 1);

        return;
      }

      const isAnyCatrgoryOfTypeRoundRobinSingleElimination = values.categories?.some(
        (c) => c.type === TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION,
      );

      if (
        values.tournament_type_id !== TournamentsTypes.ROUND_ROBIN_SINGLE_ELIMINATION &&
        !isAnyCatrgoryOfTypeRoundRobinSingleElimination
      ) {
        delete values.teams_per_group;
        delete values.teams_to_qualify_per_group;
      }

      if (values.rank_id === 0) {
        delete values.rank_id;
      }
      if (values.rank_type_id === 0) {
        delete values.rank_type_id;
      }

      const formData = createFormData(values, isAnyCatrgoryOfTypeRoundRobinSingleElimination);
      const isEdit = Boolean(selectedTournament?.id);

      toastPromise({
        request: () => {
          if (isEdit && selectedTournament?.id) {
            return updateTournament({
              id: selectedTournament?.id,
              formData,
            });
          }

          return addTournament({
            formData,
          });
        },
        onSuccess: (result) => {
          if ('error' in result) {
            throw result.error;
          }

          loadTournaments(10, page || 0);
          onClose();
          formik.resetForm();
          setActiveStep(0);

          return isEdit
            ? t('TournamentsScreen.tournamentUpdatedSuccessfully')
            : t('TournamentsScreen.tournamentAddedSuccessfully');
        },
        onError: (err) => {
          if (err.data && err.data.errors) {
            const errorMessages = Object.values(err.data.errors).flat().join('. ');

            return errorMessages;
          }
          if (err?.data?.message) {
            return err?.data?.message;
          }

          return isEdit
            ? t('TournamentsScreen.tournamentUpdateFailed')
            : t('TournamentsScreen.tournamentAdditionFailed');
        },
      });
    },
  });

  useEffect(() => {
    if (selectedTournament) {
      formik.setValues({
        name: selectedTournament.name,
        name_ar: selectedTournament.name_ar,
        venue: selectedTournament.venue,
        stadium_id: stadium.id,
        tournament_type_id: selectedTournament.tournament_type.id,
        sport_type_id: selectedTournament.sport_type.id,
        start_date: selectedTournament.start_date,
        end_date: selectedTournament.end_date,
        registration_deadline: selectedTournament.registration_deadline,
        price_per_team: String(selectedTournament.price_per_team),
        teams_per_group: Number(selectedTournament.teams_per_group),
        image: selectedTournament.tournament_image,
        teams_to_qualify_per_group: Number(selectedTournament.teams_to_qualify_per_group),
        is_live: selectedTournament.is_live,
        is_global: selectedTournament.is_global,
        is_private: selectedTournament.is_private,
        gender: selectedTournament.gender,
        prizes: selectedTournament.prizes,
        daily_timings: selectedTournament.daily_timings,
        additional_info: selectedTournament.additional_info,
        supported_payment_methods: selectedTournament.supported_payment_methods.map((method) => ({
          value: method.id,
          label: paymentMethodsArr.find((item) => item.value == method.id)?.label || '',
        })),
        categories: selectedTournament.categories.map((category: any) => ({
          id: category.id,
          name: category.name,
          name_ar: category.name_ar,
          max_number_of_teams: String(category.max_number_of_teams),
          type: category.category_type ?? null,
          show_schedule:
            typeof category.show_schedule === 'boolean' ? category.show_schedule : true,
        })),
        rank_type_id: selectedTournament.rank_type?.id,
        rank_id: selectedTournament.rank?.id,
        interface_type: selectedTournament.interface_type,
        is_waiting_list: selectedTournament.is_waiting_list ? 1 : 0,
        is_golden_point: selectedTournament.is_golden_point || 0,
        is_third_set_tie_breaker: selectedTournament.is_third_set_tie_breaker,
        show_teams: selectedTournament.config?.show_teams === 0 ? 0 : 1,
        schedule_notifications: selectedTournament?.config?.schedule_notifications === 0 ? 0 : 1,
      });
    }
  }, [selectedTournament, open]);

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSteps = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <BasicDetails selectedTournament={selectedTournament} formik={formik} />;
      case 1:
        return <AdditionalInfo formik={formik} />;
      case 2:
        return <Box pt={3}></Box>;
      default:
        break;
    }
  }, [activeStep, formik]);

  const onCloseDrawer = () => {
    onClose();
    formik.resetForm();
    setActiveStep(0);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo(0, 0);
    }
  }, [activeStep]);

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: '50%',
          padding: '20px',
        },
      }}
      className="booking-form-drawer"
      anchor="right"
      open={open}
      onClose={onCloseDrawer}
    >
      <BlankCard className="booking-form-card">
        <DrawerHeader
          title={
            selectedTournament?.id
              ? t('TournamentsScreen.editTournament')
              : t('TournamentsScreen.addTournament')
          }
          onClose={onCloseDrawer}
        />
        <form onSubmit={formik.handleSubmit}>
          <Box
            pt={4}
            sx={{
              height: 'calc(100vh - 100px)',
              overflow: 'auto',
              paddingBottom: '40px',
              paddingRight: 5,
              paddingLeft: 5,
            }}
            className="booking-form-content"
            ref={scrollRef}
          >
            <Stepper activeStep={activeStep}>
              {steps(t).map((label) => {
                const stepProps: { completed?: boolean } = {};

                return (
                  <Step key={label} {...stepProps}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <Grid mt={2} container spacing={2}>
              {handleSteps()}
              <Grid item xs={12} mt={5}>
                <Stack direction="row" spacing={2}>
                  <Button
                    color="inherit"
                    variant="contained"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    {t('AddCoachPage.back')}
                  </Button>

                  <Button type="submit" variant="contained" color={'primary'}>
                    {activeStep === steps(t)?.length - 1
                      ? t('AddCoachPage.submit')
                      : t('AddCoachPage.next')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </form>
      </BlankCard>
    </Drawer>
  );
};

export default AddEditTournamentForm;
